// Section Configurator Component
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, GripVertical, ChevronUp, ChevronDown, FileDown } from 'lucide-react';
import {
  SectionConfig,
  SECTION_PRESETS,
  HEADER_COLORS,
  generateSectionId,
  CARD_SIZE_PRESETS,
  CardSize,
  CARD_DESIGN_PRESETS,
  CardDesign,
  CARD_STYLE_PRESETS,
  CardStyle,
  PriceFormat,
  BG_PATTERN_PRESETS,
  BgPattern,
  DIVIDER_STYLE_PRESETS,
  DividerStyle,
} from './flyer-themes';

interface SectionConfiguratorProps {
  sections: SectionConfig[];
  onSectionsChange: (sections: SectionConfig[]) => void;
  isOpen: boolean;
  onClose: () => void;
  availableCategories?: string[];
}

export function SectionConfigurator({
  sections,
  onSectionsChange,
  isOpen,
  onClose,
  availableCategories = [],
}: SectionConfiguratorProps) {
  const [editingSection, setEditingSection] = useState<SectionConfig | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Add new section
  const handleAddSection = (preset?: typeof SECTION_PRESETS[0]) => {
    const newSection: SectionConfig = preset
      ? {
          ...preset,
          id: generateSectionId(),
          order: sections.length,
        }
      : {
          id: generateSectionId(),
          title: 'NEW SECTION',
          chineseTitle: '新分類',
          headerColor: 'bg-gray-600',
          bgColor: 'bg-gray-50',
          rows: 2,
          cols: 5,
          order: sections.length,
          visible: true,
        };

    onSectionsChange([...sections, newSection]);
    setIsAddingNew(false);
    setEditingSection(newSection);
  };

  // Delete section
  const handleDeleteSection = (id: string) => {
    const updated = sections
      .filter(s => s.id !== id)
      .map((s, i) => ({ ...s, order: i }));
    onSectionsChange(updated);
  };

  // Move section up/down
  const handleMoveSection = (id: string, direction: 'up' | 'down') => {
    const index = sections.findIndex(s => s.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sections.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const updated = [...sections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    onSectionsChange(updated.map((s, i) => ({ ...s, order: i })));
  };

  // Toggle section visibility
  const handleToggleVisibility = (id: string) => {
    onSectionsChange(
      sections.map(s => (s.id === id ? { ...s, visible: !s.visible } : s))
    );
  };

  // Update section
  const handleUpdateSection = (updated: SectionConfig) => {
    onSectionsChange(
      sections.map(s => (s.id === updated.id ? updated : s))
    );
    setEditingSection(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Sections</DialogTitle>
        </DialogHeader>

        {/* Section List */}
        <div className="space-y-2">
          {sections
            .sort((a, b) => a.order - b.order)
            .map((section, index) => (
              <div
                key={section.id}
                className={`flex items-center gap-2 p-3 border rounded-lg ${
                  section.visible ? 'bg-white' : 'bg-gray-100 opacity-60'
                }`}
              >
                {/* Drag Handle & Order */}
                <div className="flex items-center gap-1 text-gray-400">
                  <GripVertical className="w-4 h-4" />
                  <span className="text-xs w-4">{index + 1}</span>
                </div>

                {/* Color Preview */}
                <div className={`w-4 h-4 rounded ${section.headerColor}`} />

                {/* Title */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <p className="font-medium text-sm truncate">{section.title}</p>
                    {section.pageBreakAfter && (
                      <span className="text-[10px] bg-blue-100 text-blue-600 px-1 rounded" title="Page break after">
                        PAGE BREAK
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{section.chineseTitle} • {section.cols}×{section.rows}</p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveSection(section.id, 'up')}
                    disabled={index === 0}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveSection(section.id, 'down')}
                    disabled={index === sections.length - 1}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                  <Switch
                    checked={section.visible}
                    onCheckedChange={() => handleToggleVisibility(section.id)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingSection(section)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteSection(section.id)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
        </div>

        {/* Add Section */}
        {isAddingNew ? (
          <div className="border rounded-lg p-4 bg-gray-50">
            <p className="font-medium mb-3">Choose a preset or create custom:</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {SECTION_PRESETS.slice(0, 6).map((preset, i) => (
                <button
                  key={i}
                  onClick={() => handleAddSection(preset)}
                  className="text-left p-2 border rounded hover:bg-white transition-colors"
                >
                  <div className={`w-full h-2 rounded mb-1 ${preset.headerColor}`} />
                  <p className="text-xs font-medium">{preset.title}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddSection()}
              >
                Create Custom
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAddingNew(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsAddingNew(true)}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Section
          </Button>
        )}

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>

      {/* Edit Section Dialog */}
      {editingSection && (
        <SectionEditDialog
          section={editingSection}
          onSave={handleUpdateSection}
          onClose={() => setEditingSection(null)}
          availableCategories={availableCategories}
        />
      )}
    </Dialog>
  );
}

// ============ SECTION EDIT DIALOG ============

interface SectionEditDialogProps {
  section: SectionConfig;
  onSave: (section: SectionConfig) => void;
  onClose: () => void;
  availableCategories?: string[];
}

function SectionEditDialog({ section, onSave, onClose, availableCategories = [] }: SectionEditDialogProps) {
  const [form, setForm] = useState<SectionConfig>({ ...section });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Section</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <Label>Title (English)</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value.toUpperCase() })}
              placeholder="SECTION TITLE"
            />
          </div>

          {/* Chinese Title */}
          <div>
            <Label>Title (Chinese)</Label>
            <Input
              value={form.chineseTitle}
              onChange={(e) => setForm({ ...form, chineseTitle: e.target.value })}
              placeholder="中文標題"
            />
          </div>

          {/* Header Color */}
          <div>
            <Label>Header Color</Label>
            <Select
              value={form.headerColor}
              onValueChange={(v) => setForm({ ...form, headerColor: v })}
            >
              <SelectTrigger>
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${form.headerColor}`} />
                  <SelectValue />
                </div>
              </SelectTrigger>
              <SelectContent>
                {HEADER_COLORS.map((color) => (
                  <SelectItem key={color.value} value={color.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded ${color.value}`} />
                      {color.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Grid Size */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Columns</Label>
              <Select
                value={form.cols.toString()}
                onValueChange={(v) => setForm({ ...form, cols: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} columns
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rows</Label>
              <Select
                value={form.rows.toString()}
                onValueChange={(v) => setForm({ ...form, rows: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Card Size */}
          <div>
            <Label>Card Size</Label>
            <Select
              value={form.cardSize || 'normal'}
              onValueChange={(v) => setForm({ ...form, cardSize: v as CardSize })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CARD_SIZE_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    <div>
                      <span className="font-medium">{preset.label}</span>
                      <span className="text-xs text-gray-500 ml-2">{preset.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Card Design */}
          <div>
            <Label>Card Design</Label>
            <Select
              value={form.cardDesign || '_global'}
              onValueChange={(v) => setForm({ ...form, cardDesign: v === '_global' ? undefined : v as CardDesign })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_global">Use Global Design</SelectItem>
                {CARD_DESIGN_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    <div>
                      <span className="font-medium">{preset.label}</span>
                      <span className="text-xs text-gray-500 ml-2">{preset.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Override global card design for this section
            </p>
          </div>

          {/* Card Style */}
          <div>
            <Label>Card Style</Label>
            <Select
              value={form.cardStyle || 'flat'}
              onValueChange={(v) => setForm({ ...form, cardStyle: v as CardStyle })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CARD_STYLE_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    <div>
                      <span className="font-medium">{preset.label}</span>
                      <span className="text-xs text-gray-500 ml-2">{preset.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Price Display Options */}
          <div className="border rounded-lg p-3 space-y-2">
            <Label className="text-sm font-medium">Price Display</Label>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Show original price (strikethrough)</span>
              <Switch
                checked={form.priceFormat?.showOriginalPrice || false}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  priceFormat: { ...form.priceFormat, showOriginalPrice: checked }
                })}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Show savings amount</span>
              <Switch
                checked={form.priceFormat?.showSavings || false}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  priceFormat: { ...form.priceFormat, showSavings: checked }
                })}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Show unit price</span>
              <Switch
                checked={form.priceFormat?.showUnitPrice || false}
                onCheckedChange={(checked) => setForm({
                  ...form,
                  priceFormat: { ...form.priceFormat, showUnitPrice: checked }
                })}
              />
            </div>
          </div>

          {/* Background Pattern */}
          <div>
            <Label>Background Pattern</Label>
            <Select
              value={form.bgPattern || 'solid'}
              onValueChange={(v) => setForm({ ...form, bgPattern: v as BgPattern })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BG_PATTERN_PRESETS.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Section Divider */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium mb-0">Show Divider Below</Label>
              <Switch
                checked={form.showDivider || false}
                onCheckedChange={(checked) => setForm({ ...form, showDivider: checked })}
              />
            </div>
            {form.showDivider && (
              <Select
                value={form.dividerStyle || 'line'}
                onValueChange={(v) => setForm({ ...form, dividerStyle: v as DividerStyle })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIVIDER_STYLE_PRESETS.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Category Filter */}
          {availableCategories.length > 0 && (
            <div>
              <Label>Auto-filter by Category (optional)</Label>
              <Select
                value={form.categoryFilter || '_none'}
                onValueChange={(v) => setForm({ ...form, categoryFilter: v === '_none' ? undefined : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No filter - manual selection" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">No filter - manual selection</SelectItem>
                  {availableCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                When set, only products from this category will appear in the picker for this section
              </p>
            </div>
          )}

          {/* Page Break After */}
          <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
            <div className="flex items-center gap-2">
              <FileDown className="w-4 h-4 text-gray-500" />
              <div>
                <Label className="mb-0">Page Break After</Label>
                <p className="text-xs text-gray-500">Start a new page after this section (for multi-page flyers)</p>
              </div>
            </div>
            <Switch
              checked={form.pageBreakAfter || false}
              onCheckedChange={(checked) => setForm({ ...form, pageBreakAfter: checked })}
            />
          </div>

          {/* Preview */}
          <div className="border rounded p-2">
            <p className="text-xs text-gray-500 mb-1">Preview:</p>
            <div className={`${form.headerColor} text-white px-2 py-0.5 rounded-t text-sm font-bold`}>
              {form.title} <span className="text-xs opacity-80">{form.chineseTitle}</span>
            </div>
            <div className="bg-gray-100 p-2 rounded-b text-center text-xs text-gray-500">
              {form.cols} × {form.rows} = {form.cols * form.rows} products
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
